import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Paper,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Refresh as RefreshIcon,
  RestoreFromTrash as ResetIcon,
  Preview as PreviewIcon,
  ColorLens as ColorLensIcon,
  Image as ImageIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  CloudSync as CloudSyncIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import { 
  personalizationService, 
  PersonalizationSettings as PersonalizationSettingsType,
  WebsiteAnalysis 
} from '../services/personalizationService';

type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'error';

export const PersonalizationSettings: React.FC = () => {
  const [settings, setSettings] = useState<PersonalizationSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteAnalysis, setWebsiteAnalysis] = useState<WebsiteAnalysis | null>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  
  // Auto-save state
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedSettingsRef = useRef<string>('');

  // Auto-save function
  const performAutoSave = useCallback(async (settingsToSave: PersonalizationSettingsType) => {
    const settingsJson = JSON.stringify(settingsToSave);
    
    // Skip if nothing changed
    if (settingsJson === lastSavedSettingsRef.current) {
      return;
    }

    try {
      setSaveStatus('saving');
      console.log('🔄 Auto-saving personalization settings...');
      
      await personalizationService.updatePersonalizationSettings({
        theme: settingsToSave.theme,
        branding: settingsToSave.branding,
        customization: settingsToSave.customization
      });
      
      lastSavedSettingsRef.current = settingsJson;
      setSaveStatus('saved');
      console.log('✅ Auto-save completed');
    } catch (err: any) {
      console.error('❌ Auto-save failed:', err);
      setSaveStatus('error');
      setError('Auto-save failed: ' + (err.message || 'Unknown error'));
    }
  }, []);

  // Schedule auto-save after changes
  const scheduleAutoSave = useCallback((newSettings: PersonalizationSettingsType) => {
    setSaveStatus('unsaved');
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Schedule new save after 2 seconds
    saveTimeoutRef.current = setTimeout(() => {
      performAutoSave(newSettings);
    }, 2000);
  }, [performAutoSave]);

  useEffect(() => {
    loadSettings();
    
    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const currentSettings = await personalizationService.getPersonalizationSettings();
      setSettings(currentSettings);
      
      // Initialize lastSavedSettingsRef to prevent unnecessary saves
      lastSavedSettingsRef.current = JSON.stringify(currentSettings);
      setSaveStatus('saved');
      
      // Load current logo URL and show logo setting into state
      if (currentSettings.branding.logo) {
        setLogoUrl(currentSettings.branding.logo);
      }
      // Load show logo setting (default to true if not set)
      setShowLogo(currentSettings.branding.showLogo !== false);
    } catch (err: any) {
      setError('Failed to load personalization settings');
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError('');
      console.log('💾 Saving personalization settings:', settings);
      
      // Send the complete settings object to the API
      const updatedSettings = await personalizationService.updatePersonalizationSettings({
        theme: settings.theme,
        branding: settings.branding,
        customization: settings.customization
      });
      
      console.log('✅ Settings saved successfully:', updatedSettings);
      setSuccess('Personalization settings saved successfully! Refresh the page to see persistent changes.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      console.error('❌ Failed to save settings:', err);
      setError('Failed to save settings: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyzeWebsite = async () => {
    if (!websiteUrl.trim()) {
      setError('Please enter a website URL');
      return;
    }

    try {
      setAnalyzing(true);
      setError('');
      
      // Ensure URL has protocol
      let url = websiteUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      const analysis = await personalizationService.analyzeCompanyWebsite(url);
      setWebsiteAnalysis(analysis);
      
      // Generate personalized theme
      const personalizedSettings = await personalizationService.generatePersonalizedTheme(analysis);
      
      // Update company name from website analysis
      const updatedSettings = {
        ...personalizedSettings,
        branding: {
          ...personalizedSettings.branding,
          companyName: analysis.branding.companyName, // Use company name from analysis
        }
      };
      
      setSettings(updatedSettings);
      
      // Apply the theme immediately to DOM with branding information
      personalizationService.applyThemeToDOM(updatedSettings.theme, updatedSettings.branding);
      
      // Dispatch event to notify other components
      const event = new CustomEvent('personalization-updated', {
        detail: updatedSettings
      });
      window.dispatchEvent(event);
      
      setSuccess('Website analyzed and theme generated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to analyze website: ' + (err.message || 'Unknown error'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleResetToDefaults = async () => {
    try {
      setSaving(true);
      setError('');
      await personalizationService.resetToDefaults();
      await loadSettings();
      setSuccess('Settings reset to defaults successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to reset settings: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // For text inputs - only update state, no DOM changes (prevents losing focus)
  const handleThemeChangeText = (field: string, value: any) => {
    if (!settings) return;
    setSettings({
      ...settings,
      theme: {
        ...settings.theme,
        [field]: value,
      },
    });
  };

  // For color/select inputs - update state, apply to DOM, and schedule auto-save
  const handleThemeChange = (field: string, value: any) => {
    if (!settings) return;
    const updatedSettings = {
      ...settings,
      theme: {
        ...settings.theme,
        [field]: value,
      },
    };
    setSettings(updatedSettings);
    
    // Apply changes immediately to DOM
    personalizationService.applyThemeToDOM(updatedSettings.theme, updatedSettings.branding);
    
    // Dispatch event to notify other components (like ModernNavigation)
    const event = new CustomEvent('personalization-updated', {
      detail: updatedSettings
    });
    window.dispatchEvent(event);
    
    // Schedule auto-save
    scheduleAutoSave(updatedSettings);
  };

  const handleCustomizationChange = (field: string, value: any) => {
    if (!settings) return;
    const updatedSettings = {
      ...settings,
      customization: {
        ...settings.customization,
        [field]: value,
      },
    };
    setSettings(updatedSettings);
    
    // Apply changes immediately and notify other components
    personalizationService.applyThemeToDOM(updatedSettings.theme, updatedSettings.branding);
    
    // Dispatch event to notify other components (like ModernNavigation)
    const event = new CustomEvent('personalization-updated', {
      detail: updatedSettings
    });
    window.dispatchEvent(event);
    
    // Schedule auto-save
    scheduleAutoSave(updatedSettings);
  };

  // For text inputs - only update state, no DOM changes (prevents losing focus)
  const handleBrandingChangeText = (field: string, value: any) => {
    if (!settings) return;
    setSettings({
      ...settings,
      branding: {
        ...settings.branding,
        [field]: value,
      },
    });
  };

  // For non-text inputs - update state AND apply immediately
  const handleBrandingChange = (field: string, value: any) => {
    if (!settings) return;
    const updatedSettings = {
      ...settings,
      branding: {
        ...settings.branding,
        [field]: value,
      },
    };
    setSettings(updatedSettings);
    
    // Apply changes immediately and notify other components
    personalizationService.applyThemeToDOM(updatedSettings.theme, updatedSettings.branding);
    
    // Dispatch event to notify other components (like ModernNavigation)
    const event = new CustomEvent('personalization-updated', {
      detail: updatedSettings
    });
    window.dispatchEvent(event);
    
    // Schedule auto-save
    scheduleAutoSave(updatedSettings);
  };

  const handleLogoUrlChange = (url: string) => {
    setLogoUrl(url);
    handleBrandingChange('logo', url);
  };

  const handleLogoFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('📁 File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    try {
      setLogoUploading(true);
      setError('');

      // Create a data URL for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        console.log('📸 Data URL created:', dataUrl.substring(0, 50) + '...');
        
        // Update both states immediately
        setLogoUrl(dataUrl);
        handleBrandingChange('logo', dataUrl);
        
        console.log('✅ Logo states updated');
        setSuccess('Logo uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      };
      
      reader.onerror = (error) => {
        console.error('❌ FileReader error:', error);
        setError('Failed to read the file');
      };
      
      reader.readAsDataURL(file);
    } catch (err: any) {
      console.error('❌ Upload error:', err);
      setError('Failed to upload logo: ' + (err.message || 'Unknown error'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
    handleBrandingChange('logo', '');
    setSuccess('Logo removed successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load personalization settings</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <PaletteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Personalization Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Customize the appearance and branding of your time tracker to match your company's identity.
          </Typography>
        </Box>
        
        {/* Auto-save status indicator */}
        <Chip
          icon={
            saveStatus === 'saved' ? <CheckCircleIcon /> :
            saveStatus === 'saving' ? <CloudSyncIcon /> :
            saveStatus === 'error' ? <SaveIcon /> :
            <SaveIcon />
          }
          label={
            saveStatus === 'saved' ? 'All changes saved' :
            saveStatus === 'saving' ? 'Saving...' :
            saveStatus === 'error' ? 'Save failed' :
            'Unsaved changes'
          }
          color={
            saveStatus === 'saved' ? 'success' :
            saveStatus === 'saving' ? 'info' :
            saveStatus === 'error' ? 'error' :
            'warning'
          }
          variant="outlined"
          sx={{ 
            fontSize: '0.875rem',
            animation: saveStatus === 'saving' ? 'pulse 1s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.5 },
              '100%': { opacity: 1 },
            }
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Website Analysis Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <ColorLensIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              AI-Powered Website Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Enter your company's website URL to automatically generate a personalized theme based on your brand.
            </Typography>
            
            <Box display="flex" gap={2} alignItems="center" mb={2}>
              <TextField
                fullWidth
                label="Company Website URL"
                placeholder="https://yourcompany.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                disabled={analyzing}
              />
              <Button
                variant="contained"
                onClick={handleAnalyzeWebsite}
                disabled={analyzing || !websiteUrl.trim()}
                startIcon={analyzing ? <CircularProgress size={20} /> : <PreviewIcon />}
                sx={{ minWidth: 140 }}
              >
                {analyzing ? 'Analyzing...' : 'Analyze'}
              </Button>
            </Box>

            {websiteAnalysis && (
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Analysis Results:
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2">
                    <strong>Company:</strong> {websiteAnalysis.branding.companyName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Industry:</strong> {websiteAnalysis.branding.industry}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Style:</strong> {websiteAnalysis.branding.style}
                  </Typography>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      <strong>Brand Colors:</strong>
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label="Primary"
                        size="small"
                        sx={{ bgcolor: websiteAnalysis.colors.primary, color: 'white' }}
                      />
                      <Chip
                        label="Secondary"
                        size="small"
                        sx={{ bgcolor: websiteAnalysis.colors.secondary, color: 'white' }}
                      />
                      <Chip
                        label="Accent"
                        size="small"
                        sx={{ bgcolor: websiteAnalysis.colors.accent, color: 'white' }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>

        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Theme Colors */}
          <Card sx={{ flex: 1, minWidth: 300 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Theme Colors
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Primary Color"
                  type="color"
                  value={settings.theme.primaryColor}
                  onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Secondary Color"
                  type="color"
                  value={settings.theme.secondaryColor}
                  onChange={(e) => handleThemeChange('secondaryColor', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Accent Color"
                  type="color"
                  value={settings.theme.accentColor}
                  onChange={(e) => handleThemeChange('accentColor', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Background Color"
                  type="color"
                  value={settings.theme.backgroundColor}
                  onChange={(e) => handleThemeChange('backgroundColor', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Typography & Layout */}
          <Card sx={{ flex: 1, minWidth: 300 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Typography & Layout
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>Font Family</InputLabel>
                  <Select
                    value={settings.theme.fontFamily}
                    onChange={(e) => handleThemeChange('fontFamily', e.target.value)}
                    label="Font Family"
                  >
                    <MenuItem value="Roboto, Arial, sans-serif">Roboto</MenuItem>
                    <MenuItem value="Inter, Arial, sans-serif">Inter</MenuItem>
                    <MenuItem value="Poppins, Arial, sans-serif">Poppins</MenuItem>
                    <MenuItem value="Open Sans, Arial, sans-serif">Open Sans</MenuItem>
                    <MenuItem value="Lato, Arial, sans-serif">Lato</MenuItem>
                    <MenuItem value="Montserrat, Arial, sans-serif">Montserrat</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Border Radius"
                  value={settings.theme.borderRadius}
                  onChange={(e) => handleThemeChangeText('borderRadius', e.target.value)}
                  placeholder="8px"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.theme.shadows}
                      onChange={(e) => handleThemeChange('shadows', e.target.checked)}
                    />
                  }
                  label="Enable Shadows"
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Branding */}
          <Card sx={{ flex: 1, minWidth: 300 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Branding
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={settings.branding.companyName || ''}
                  onChange={(e) => handleBrandingChangeText('companyName', e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Heading Font"
                  value={settings.branding.typography.headingFont}
                  onChange={(e) => handleBrandingChangeText('typography', {
                    ...settings.branding.typography,
                    headingFont: e.target.value
                  })}
                />
                <TextField
                  fullWidth
                  label="Body Font"
                  value={settings.branding.typography.bodyFont}
                  onChange={(e) => handleBrandingChangeText('typography', {
                    ...settings.branding.typography,
                    bodyFont: e.target.value
                  })}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showLogo}
                      onChange={(e) => {
                        setShowLogo(e.target.checked);
                        handleBrandingChange('showLogo', e.target.checked);
                      }}
                    />
                  }
                  label="Show Logo in Navigation"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Customization */}
          <Card sx={{ flex: 1, minWidth: 300 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Interface Customization
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>Menu Style</InputLabel>
                  <Select
                    value={settings.customization.menuStyle}
                    onChange={(e) => handleCustomizationChange('menuStyle', e.target.value)}
                    label="Menu Style"
                  >
                    <MenuItem value="sidebar">Sidebar</MenuItem>
                    <MenuItem value="topbar">Top Bar</MenuItem>
                    <MenuItem value="compact">Compact</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Card Style</InputLabel>
                  <Select
                    value={settings.customization.cardStyle}
                    onChange={(e) => handleCustomizationChange('cardStyle', e.target.value)}
                    label="Card Style"
                  >
                    <MenuItem value="elevated">Elevated</MenuItem>
                    <MenuItem value="outlined">Outlined</MenuItem>
                    <MenuItem value="filled">Filled</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Density</InputLabel>
                  <Select
                    value={settings.customization.density}
                    onChange={(e) => handleCustomizationChange('density', e.target.value)}
                    label="Density"
                  >
                    <MenuItem value="comfortable">Comfortable</MenuItem>
                    <MenuItem value="compact">Compact</MenuItem>
                    <MenuItem value="spacious">Spacious</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.customization.animations}
                      onChange={(e) => handleCustomizationChange('animations', e.target.checked)}
                    />
                  }
                  label="Enable Animations"
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Logo Management Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <ImageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Company Logo
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload your company logo or provide a URL to display it in the navigation bar.
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              {/* Debug Info */}
              {process.env.NODE_ENV === 'development' && (
                <Paper sx={{ p: 1, bgcolor: 'yellow.50', fontSize: '12px' }}>
                  <strong>Debug:</strong> logoUrl="{logoUrl}", settings.branding.logo="{settings.branding.logo || 'empty'}"
                </Paper>
              )}

              {/* Current Logo Preview */}
              {(settings.branding.logo || logoUrl) && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Current Logo:
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        border: '2px dashed #ccc',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        bgcolor: 'white',
                      }}
                    >
                      {(settings.branding.logo || logoUrl) && (
                        <img
                          key={`logo-${Date.now()}`} // Force re-render with timestamp
                          src={settings.branding.logo || logoUrl}
                          alt="Company Logo"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                          onLoad={() => {
                            console.log('🖼️ Logo loaded successfully');
                          }}
                          onError={() => {
                            console.error('❌ Logo failed to load:', settings.branding.logo || logoUrl);
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {(settings.branding.logo || logoUrl).length > 50 
                          ? `${(settings.branding.logo || logoUrl).substring(0, 50)}...` 
                          : (settings.branding.logo || logoUrl)}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRemoveLogo}
                        startIcon={<DeleteIcon />}
                      >
                        Remove Logo
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )}

              <Box display="flex" gap={3} flexWrap="wrap">
                {/* Upload Logo */}
                <Card sx={{ flex: 1, minWidth: 300 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Upload Logo File
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Upload an image file (PNG, JPG, SVG). Max size: 5MB.
                    </Typography>
                    
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="logo-upload"
                      type="file"
                      onChange={handleLogoFileUpload}
                      disabled={logoUploading}
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="contained"
                        component="span"
                        disabled={logoUploading}
                        startIcon={logoUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                        fullWidth
                      >
                        {logoUploading ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </label>
                  </CardContent>
                </Card>

                {/* Logo URL */}
                <Card sx={{ flex: 1, minWidth: 300 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Logo URL
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Enter a direct URL to your logo image.
                    </Typography>
                    
                    <Box display="flex" gap={2} alignItems="center">
                      <TextField
                        fullWidth
                        label="Logo URL"
                        placeholder="https://example.com/logo.png"
                        value={logoUrl}
                        onChange={(e) => setLogoUrl(e.target.value)}
                        size="small"
                      />
                      <Button
                        variant="outlined"
                        onClick={() => handleLogoUrlChange(logoUrl)}
                        disabled={!logoUrl.trim()}
                        sx={{ minWidth: 100 }}
                      >
                        Apply
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Logo Guidelines */}
              <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Logo Guidelines:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>Recommended size: 200x60 pixels or similar aspect ratio</li>
                  <li>Supported formats: PNG, JPG, SVG, WebP</li>
                  <li>Transparent background recommended for PNG files</li>
                  <li>Logo will be automatically resized to fit the navigation bar</li>
                </Box>
              </Paper>
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardContent>
            <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={handleResetToDefaults}
                disabled={saving}
                startIcon={<ResetIcon />}
              >
                Reset to Defaults
              </Button>
              <Button
                variant="outlined"
                onClick={loadSettings}
                disabled={saving}
                startIcon={<RefreshIcon />}
              >
                Reload Settings
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
